import type { ElementType, ComponentPropsWithoutRef } from 'react'
import * as s from './text.css'

function cx(...parts: (string | false | undefined)[]) {
  return parts.filter(Boolean).join(' ')
}

type Tone = 'ink' | 'ink2' | 'soft' | 'red' | 'amber' | 'green'
type Any = ComponentPropsWithoutRef<'p'>

export function Text({
  size,
  tone,
  leading,
  weight,
  italic,
  as: As = 'p',
  className,
  ...rest
}: Any & {
  size?: 'lg' | 'md' | 'sm' | 'base'
  tone?: Tone
  leading?: 'tight' | 'snug' | 'normal' | 'relaxed'
  weight?: 'normal' | 'medium'
  italic?: boolean
  as?: ElementType
}) {
  return <As className={cx(s.text({ size, tone, leading, weight, italic }), className)} {...rest} />
}

export function Heading({
  level,
  tone,
  weight,
  italic,
  as: As = 'h2',
  className,
  ...rest
}: Any & {
  level?: '3xl' | '2xl' | 'xl' | 'lg'
  tone?: Tone
  weight?: 'normal' | 'medium'
  italic?: boolean
  as?: ElementType
}) {
  return <As className={cx(s.heading({ level, tone, weight, italic }), className)} {...rest} />
}

type LabelProps = Any & { size?: 'sm'; tone?: Tone; tracked?: boolean; as?: ElementType }
export function Label({ size, tone, tracked, as: As = 'span', className, ...rest }: LabelProps) {
  return <As className={cx(s.label({ size, tone, tracked }), className)} {...rest} />
}

export function Count({
  size,
  tone,
  as: As = 'span',
  className,
  ...rest
}: Any & { size?: 'lg' | 'md' | 'sm'; tone?: Tone; as?: ElementType }) {
  return <As className={cx(s.count({ size, tone }), className)} {...rest} />
}

// Kicker — the editorial eyebrow above a headline or section.
export function Kicker({ className, as: As = 'p', ...rest }: Any & { as?: ElementType }) {
  return <As className={cx(s.kicker, className)} {...rest} />
}

// SectionHead — the mono heading that opens a ruled block.
export function SectionHead({ className, as: As = 'h3', ...rest }: Any & { as?: ElementType }) {
  return <As className={cx(s.sectionHead, className)} {...rest} />
}

// StateLine — "…" while a surface is loading, "—" when it could not load, or a sentence when
// there is genuinely nothing to show. `busy` marks it for assistive tech; the geometry is the
// same either way, so the page does not jump between the two.
export function StateLine({ busy, className, ...rest }: Any & { busy?: boolean; as?: ElementType }) {
  return <p aria-busy={busy || undefined} className={cx(s.stateLine, className)} {...rest} />
}

// Gloss — the other-language name trailing the primary one.
export function Gloss({ className, as: As = 'em', ...rest }: Any & { as?: ElementType }) {
  return <As className={cx(s.gloss, className)} {...rest} />
}
